import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button'; // Assuming shadcn/ui button
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'; // Assuming shadcn/ui card
import { ExhibitCard } from '@/museum-app/features/exhibits/components/exhibit-card'; // Assuming ExhibitCard path
import { Exhibit } from '@/museum-app/types/models'; // Assuming Exhibit type path
import axios from 'axios'; // Or your preferred API client
import { LayoutGrid, Image, Database, Map, QrCode } from 'lucide-react'; // Icons

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [featuredExhibits, setFeaturedExhibits] = useState<Exhibit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // TODO: Fetch actual featured exhibits from an API endpoint like /api/v1/exhibits/featured
  useEffect(() => {
    // Simulating API call for now
    const fetchFeatured = async () => {
      setIsLoading(true);
      try {
        // Replace with actual API call
        // const response = await axios.get<{ data: Exhibit[] }>('/api/v1/exhibits/featured');
        // setFeaturedExhibits(response.data.data || []);

        // Placeholder data (structure matches Exhibit type):
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
        setFeaturedExhibits([
          // Example placeholder structure (currently empty):
          // { id: 1, translations: [{ language_code: 'en', title: 'Placeholder Exhibit 1', description: 'Desc...' }], thumbnail_url: null, status: ExhibitStatus.Published, media: {}, created_at: '', updated_at: '', default_language: 'en', rooms: [] },
        ]);

      } catch (error) {
        console.error("Failed to fetch featured exhibits:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const quickAccessItems = [
    { path: '/rooms', label: t('navigation.rooms', 'Rooms'), icon: LayoutGrid },
    { path: '/exhibits', label: t('navigation.exhibits', 'Exhibits'), icon: Image },
    { path: '/tours', label: t('navigation.tours', 'Tours'), icon: Database },
    { path: '/map', label: t('navigation.map', 'Museum Map'), icon: Map }, // Added Map
    { path: '/scan', label: t('navigation.scanQr', 'Scan QR'), icon: QrCode },
  ];

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[300px] w-full overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-800">
        {/* Placeholder for image/carousel */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 p-8 text-center text-white">
          <h1 className="mb-4 text-4xl font-bold md:text-5xl">
            {t('home.welcomeTitle', 'Welcome to the SK Museum')}
          </h1>
          <p className="mb-6 max-w-xl text-lg">
            {t('home.welcomeSubtitle', 'Explore our collections and plan your visit.')}
          </p>
          <Button size="lg" asChild>
            <Link to="/scan">{t('navigation.scanQr', 'Scan QR Code')}</Link>
          </Button>
        </div>
      </section>

      {/* Featured Exhibits Section */}
      <section aria-labelledby="featured-exhibits-heading">
        <h2 id="featured-exhibits-heading" className="mb-6 text-2xl font-semibold md:text-3xl">
          {t('home.featuredExhibitsTitle', 'Featured Exhibits')}
        </h2>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Skeleton Loaders */}
            <div className="h-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
            <div className="h-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
          </div>
        ) : featuredExhibits.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {featuredExhibits.map((exhibit) => (
              <ExhibitCard key={exhibit.id} exhibit={exhibit} /> // Pass the full exhibit object
            ))}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400">{t('home.noFeaturedExhibits', 'No featured exhibits available at the moment.')}</p>
        )}
      </section>

      {/* Quick Access Section */}
      <section aria-labelledby="quick-access-heading">
        <h2 id="quick-access-heading" className="mb-6 text-2xl font-semibold md:text-3xl">
          {t('home.quickAccessTitle', 'Explore')}
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {quickAccessItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="group flex flex-col items-center justify-center rounded-lg border bg-card p-6 text-card-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:border-neutral-800"
            >
              <item.icon className="mb-3 h-8 w-8 text-blue-600 transition-colors group-hover:text-accent-foreground dark:text-blue-400" />
              <span className="text-center text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
